/**
 * Shared steps for highlighting a citation in a rendered document's DOM, used
 * by the DOCX and PPTX viewers. Each viewer keeps its own loop over the nodes
 * to highlight, because they handle a failed node replacement differently.
 */

import { findTextMatch, normalizeText } from "@/utils/textMatching";

/** The head and tail segments of a citation are at most this long. */
const MATCH_SEGMENT_LENGTH = 100;

/** How long to wait for DOM updates before scrolling to the first highlight. */
const SCROLL_DELAY_MS = 100;

const isWordChar = (ch: string) => /[\p{L}\p{N}]/u.test(ch);

/** Every text node in the container that holds more than whitespace. */
export function collectTextNodes(container: HTMLElement): Text[] {
  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  let node;

  while ((node = walker.nextNode())) {
    if (node.nodeValue && node.nodeValue.trim().length > 0) {
      textNodes.push(node as Text);
    }
  }

  return textNodes;
}

/** The document's normalised text, and which text node each character came from. */
export interface TextNodeIndex {
  fullText: string;
  /** Node index per character of `fullText`, or -1 for an inserted separator. */
  charToNodeIndex: number[];
}

/**
 * Build normalised text with character-to-node mapping (same approach as the
 * PDF viewer). A space is inserted between adjacent nodes only where two word
 * characters would otherwise run together, and never after a hyphen.
 */
export function buildTextNodeIndex(textNodes: Text[]): TextNodeIndex {
  const charToNodeIndex: number[] = [];
  let fullText = "";

  textNodes.forEach((node, idx) => {
    const curr = normalizeText(node.nodeValue || "");
    const prev = idx > 0 ? normalizeText(textNodes[idx - 1].nodeValue || "") : "";
    const prevLast = prev.slice(-1);
    const currFirst = curr.slice(0, 1);
    const shouldInsertSpace =
      idx > 0 && isWordChar(prevLast) && isWordChar(currFirst) && prevLast !== "-";

    if (shouldInsertSpace) {
      fullText += " ";
      charToNodeIndex.push(-1); // separator, belongs to no node
    }

    // Append current normalised text and map each character to this node index
    for (let k = 0; k < curr.length; k++) {
      fullText += curr[k];
      charToNodeIndex.push(idx);
    }
  });

  return { fullText, charToNodeIndex };
}

/**
 * The node a character belongs to. On a separator, scan in the preferred
 * direction first and then the other way; -1 when no node is found.
 */
export function nearestNodeIndex(
  charToNodeIndex: number[],
  pos: number,
  forwardFirst: boolean,
): number {
  const idx = charToNodeIndex[pos];

  if (idx >= 0) return idx;

  const scan = (step: 1 | -1) => {
    for (let p = pos; p >= 0 && p < charToNodeIndex.length; p += step) {
      if (charToNodeIndex[p] >= 0) return charToNodeIndex[p];
    }

    return -1;
  };
  const first = scan(forwardFirst ? 1 : -1);

  return first >= 0 ? first : scan(forwardFirst ? -1 : 1);
}

/**
 * The first and last text node to highlight, found by matching the citation's
 * head and tail segments. Where a segment does not match, the range runs to
 * the start or end of the document. The end scans backwards first, to avoid
 * overshooting.
 */
export function findCitationNodeRange(
  { fullText, charToNodeIndex }: TextNodeIndex,
  nodeCount: number,
  citationText: string,
): { startNodeIndex: number; endNodeIndex: number } {
  const headLen = Math.min(MATCH_SEGMENT_LENGTH, citationText.length);
  const headSeg = citationText.substring(0, headLen);
  const tailLen = Math.min(MATCH_SEGMENT_LENGTH, citationText.length);
  const tailSeg = citationText.substring(Math.max(0, citationText.length - tailLen));

  const headMatch = findTextMatch(fullText, headSeg);
  const tailMatch = findTextMatch(fullText, tailSeg);
  const clamp = (pos: number) => Math.max(0, Math.min(pos, fullText.length - 1));

  let startNodeIndex = 0;
  let endNodeIndex = nodeCount - 1;

  if (headMatch) {
    const idx = nearestNodeIndex(charToNodeIndex, clamp(headMatch.position), true);

    if (idx >= 0) startNodeIndex = idx;
  }

  if (tailMatch) {
    const endPos = clamp(tailMatch.position + tailMatch.length - 1);
    const idx = nearestNodeIndex(charToNodeIndex, endPos, false);

    if (idx >= 0) endNodeIndex = idx;
  }

  return { startNodeIndex, endNodeIndex };
}

/** A <mark> holding the given text, styled by Tailwind so dark mode works. */
export function createHighlightMark(textContent: string): HTMLElement {
  const mark = document.createElement("mark");

  mark.className = "bg-warning/30 dark:bg-warning/40 px-1 rounded";
  mark.style.padding = "2px 4px";
  mark.style.borderRadius = "2px";
  mark.textContent = textContent;

  return mark;
}

/** Scroll to the first highlight once DOM updates are complete, smoothly. */
export function scrollToHighlight(mark: HTMLElement | undefined): void {
  if (!mark) return;

  setTimeout(() => {
    mark.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
  }, SCROLL_DELAY_MS);
}
